/**
 * Loading state of the add-to-cart buttons.
 *
 * The class is what makes a button unclickable (".loading { pointer-events: none }"),
 * so every path that sets it needs a guaranteed way back. It must never hang off
 * the click itself: YITH WAPO validates required add-ons in a delegated handler on
 * "document" (front.js, "$(document).on('click', 'form.cart button')" returning
 * false), and the theme listens on document.body - one level lower - so the class
 * would always be set before the validation cancels the click, with no later event
 * left to clear it again.
 *
 * "adding_to_cart", "added_to_cart", "wc_cart_button_updated" and "ajaxError" are
 * jQuery custom events: jQuery.trigger() walks its own handler list instead of
 * dispatching a DOM event, so addEventListener never sees them and those three
 * listeners have to stay on jQuery. The submit is a real DOM event.
 */

const STUCK_SINGLE = ".single_add_to_cart_button.loading";
const STUCK_ANY = ".single_add_to_cart_button.loading, .add_to_cart_button.loading";

// Nothing legitimate keeps an add to cart pending this long. Whatever still spins
// after that lost its response - a failed request, or a plugin cancelling the
// submit - so free the button instead of leaving it dead until a page reload.
const WATCHDOG_MS = 15000;

// Add-to-cart requests we can recognise by URL. Failing one of those frees the
// button immediately, without waiting for the watchdog.
const ADD_TO_CART_REQUESTS = ["wc-ajax=add_to_cart", "yith_wccl_add_to_cart"];

let watchdog = null;

const sweep = (selector) => {
  document
    .querySelectorAll(selector)
    .forEach((button) => button.classList.remove("loading"));
};

const cancelWatchdog = () => {
  if (watchdog) {
    clearTimeout(watchdog);
    watchdog = null;
  }
};

const startWatchdog = () => {
  cancelWatchdog();
  watchdog = setTimeout(() => {
    watchdog = null;
    sweep(STUCK_ANY);
  }, WATCHDOG_MS);
};

export const setupAddToCartLoading = () => {
  // AJAX add to cart: loop swatches (YITH Color Label Variations) and the
  // WooCommerce loop buttons.
  jQuery(document.body).on("adding_to_cart", (e, btn) => {
    btn?.[0]?.classList.add("loading");
    startWatchdog();
  });

  // Form add to cart: single product and quick view. Bound to the submit, which
  // only happens once every validation passed - unlike the click.
  document.addEventListener("submit", (event) => {
    const form = event.target;

    if (!(form instanceof Element) || !form.matches("form.cart")) {
      return;
    }

    const button = form.querySelector(
      ".single_add_to_cart_button:not(.disabled):not(.added)"
    );

    if (!button) {
      return;
    }

    button.classList.add("loading");
    startWatchdog();
  });

  jQuery(document.body).on(
    "added_to_cart wc_cart_button_updated",
    (e, fragments, hash, btn) => {
      cancelWatchdog();
      btn?.[0]?.classList.remove("loading");
      sweep(STUCK_SINGLE);
    }
  );

  // The AJAX add to cart of YITH Color Label Variations only restores the button
  // in its success callback, so a failed request would leave it spinning.
  jQuery(document).on("ajaxError", (event, xhr, settings) => {
    const url = settings?.url || "";

    if (!ADD_TO_CART_REQUESTS.some((endpoint) => url.indexOf(endpoint) !== -1)) {
      return;
    }

    cancelWatchdog();
    sweep(STUCK_ANY);
  });

  // Back navigation restores the page from the bfcache exactly as it was left -
  // including a button still marked as loading from the click that led away.
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      sweep(STUCK_ANY);
    }
  });
};
